import { streamRng, chance } from '../math/rng.js';
import { BRAIN, PED, STATE } from '../actors/ai.js';

/**
 * @file SPEECH BUBBLES — who is talking, what they say, and how much of it has
 * been typed. THE MODEL ONLY.
 *
 * There is a renderer for this (`render/SpeechBubbles.js`) and it lives in a
 * different file on purpose. Everything below is arithmetic over the manager's
 * flat arrays: a candidate scan, a seeded line pick, a typewriter clock and a
 * fade. Nothing here knows what a quad is, what an atlas cell measures, or
 * which way the camera is pointing — it publishes {@link Bubble} records and
 * the renderer draws them. That is the same split `Traffic`/`Driving`/
 * `Carjack`/`VehicleDamage` make, and it buys the same thing: the whole
 * behaviour — the cadence, the cooldowns, the priority, the determinism — is
 * provable in node with duck types, at no GPU and no browser. A bubble system
 * fused to its renderer can only be tested by looking at it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS
 * ─────────────────────────────────────────────────────────────────────────────
 * Two imports, both plain arithmetic: `math/rng.js` and `actors/ai.js`'s enums.
 * The manager, the bus, the CONTENT (`script`), the faction table and the
 * wanted level are all INJECTED and every one of them may be null — the
 * `VehicleDamage` discipline. With a null `script` the system is inert but
 * still steps: it reconciles and retires what is already open and never draws
 * a die. A dev scene with no faction rules still gets bubbles, because a world
 * with no factions is a world of civilians.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SIX THINGS THAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **A SLOT INDEX IS NOT AN IDENTITY, and the guard is TWO mechanisms.**
 *   `EnemyManager` frees slots onto a LIFO list and the population director
 *   refills them inside the SAME step, so a record keyed on the raw index will
 *   be inherited by a fresh pedestrian halfway through somebody else's
 *   sentence — the pedestrian equivalent of the `(handle, serial)` note in
 *   `docs/vehicles.md`, where a freed fleet handle came back the same frame and
 *   a new car inherited the wreck's 4 hp. So: {@link createSpeech.onSlotFreed}
 *   is the EXACT signal (Gameplay fans out to it explicitly — we deliberately
 *   do NOT chain `manager.onDespawn`; `Shops.js` already owns a link in that
 *   chain and a second one would be two files fighting over one property), and
 *   the per-step reconcile re-checks `state[i] !== DEAD && typeIdx[i] ===
 *   rec.typeIdx` as the BACKSTOP. Neither alone is enough: a caller that forgets
 *   to wire the fan-out still cannot resurrect a bubble onto a stranger, and
 *   the type check still cannot see a recycle into the same type — which is
 *   exactly what the exact signal is for. Both, always.
 *
 * • **AN IDLE FRAME DRAWS NO RNG.** The scan decides there IS an eligible
 *   candidate and that the cap and the gap allow an opening BEFORE it touches
 *   {@link SPEECH.speakChance}. Rule 4 in CLAUDE.md is "same seed, same city";
 *   a die rolled on a frame where nobody spoke makes the stream depend on how
 *   long the player stood still, and every later draw in the `'dialogue'`
 *   stream moves with it. Three draws per opening — chance, candidate, line —
 *   and zero the rest of the time.
 *
 * • **THE DISTANCE TEST IS 3D.** A shop interior is a real room 100 m under the
 *   island (`docs/interiors.md`), so an XZ test would let a grocer chat to a
 *   player standing on the pavement above his ceiling, and would silence
 *   nobody in the street while the player is downstairs. The 3D test does both
 *   jobs with one comparison: in the room with them, you hear them; on the
 *   street above, you do not. Same reasoning as the blast test in
 *   `VehicleDamage`, which had to become 3D for the same buried crowd.
 *
 * • **A SEATED SLOT NEVER SPEAKS.** `docs/factions.md`: a car is DRAWN EMPTY —
 *   `inCabin` takes the occupant off screen entirely — so a bubble over a
 *   moving saloon is a caption on an empty car. {@link topicFor} refuses
 *   `BRAIN.SEATED` and the reconcile retires any bubble whose speaker gets into
 *   a car mid-sentence. When cars grow a cabin interior this is one line, and
 *   it is deliberately not that line today.
 *
 * • **THE TYPEWRITER IS DERIVED FROM ONE `t`, NEVER ACCUMULATED.** `chars` and
 *   `alpha` are pure functions of the record's age: `chars = floor(t * charHz)`,
 *   and the fade reads off two fixed instants computed at open time. An
 *   accumulator (`chars += charHz * dt`) drifts against the hold and the fade
 *   the moment a frame is long or the perf governor halves the render rate, and
 *   the drift is invisible until a line finishes typing after it has faded out.
 *   One clock, three derived quantities, no way for them to disagree.
 *
 * • **NO BUS SUBSCRIPTIONS.** v1 derives the topic from live state on every
 *   scan, which is strictly more robust than reacting to an event: a bubble is
 *   correct for what the speaker is doing NOW, not for what they were doing
 *   when a message was posted, and the system has no subscription to leak and
 *   no ordering to get wrong against `Population`'s emitters. `bus` is accepted
 *   and unused for exactly this reason. An event-driven reaction to `'crime'`
 *   ("Did you see that?!") is a BACKLOG line, not a gap.
 *
 * @module engine/systems/Speech
 */

/** @typedef {import('../actors/EnemyManager.js').EnemyManager} EnemyManager */
/** @typedef {import('./Population.js').FactionRules} FactionRules */

/**
 * @typedef {Object} BubbleStyle CSS colour strings, straight from the content.
 * @property {string} bg @property {string} fg @property {string} accent
 */

/**
 * ONE live bubble. The renderer reads ONLY these fields.
 * @typedef {Object} Bubble
 * @property {number} slot Manager slot of the speaker.
 * @property {number} cell Atlas cell / batch instance this bubble owns. STABLE
 *   for the life of the record, and freed the moment it retires.
 * @property {number} x @property {number} y @property {number} z World position
 *   of the TAIL TIP — the bubble's BOTTOM, above the speaker's head. Re-read
 *   every step so it follows a walking NPC.
 * @property {string} text The whole line.
 * @property {number} chars How much of it has been typed, 0..`text.length`.
 * @property {number} alpha 0..1.
 * @property {BubbleStyle} style
 * @property {string} topic The {@link topicFor} key that opened it.
 */

/**
 * Tuning. Metres, seconds, Hz. Every number says what breaks if it moves.
 */
export const SPEECH = Object.freeze({
  /** Concurrent bubbles == atlas cells == batch instances. The renderer sizes
   *  its atlas from this, so raising it without raising the atlas is bubbles
   *  drawing each other's text. */
  maxLive: 5,
  /** 3D metres from the PLAYER; beyond it nobody speaks. Wider and the city
   *  murmurs from behind buildings you cannot see; tighter and a bubble only
   *  ever opens on somebody already in your face. */
  radius: 30,
  /** Typewriter reveal rate, characters per second. Below ~18 a 26-character
   *  line outlives its own hold; above ~40 there is nothing to read, the text
   *  just appears. */
  charHz: 26,
  /** Seconds the finished line is held... Drop it and a short line is gone
   *  before the eye reaches it. */
  holdMin: 1.4,
  /** ...plus this per character, so a LONG line reads. Without the per-char
   *  term every line holds for the same time and the long ones are unreadable
   *  while the short ones loiter. */
  holdPerChar: 0.055,
  /** Ramp up. Long enough not to pop, short enough that it is over before the
   *  first characters land. */
  fadeIn: 0.10,
  /** Ramp down. Longer than `fadeIn` on purpose — an appearance is an event, a
   *  disappearance should not be. */
  fadeOut: 0.30,
  /** Seconds before the SAME slot may speak again. This is what stops one
   *  pedestrian monologuing at a stationary player; at ~5 s a single NPC can
   *  hold two of the five cells for a minute. */
  slotCooldown: 16,
  /** Minimum seconds between two bubbles opening ANYWHERE. The whole cast
   *  becoming eligible on one step (a crowd panicking) must read as a ripple,
   *  not as five bubbles blinking on together. */
  openGap: 0.8,
  /** Candidate scans per second. It is an O(top) walk; 3 Hz is free, and much
   *  higher only spends dice faster because `speakChance` rolls per scan. */
  scanHz: 3,
  /** Probability the chosen candidate actually opens. THE density dial: at 1.0
   *  a bubble opens every `openGap` for as long as anyone is in range, which
   *  is a city of people talking to themselves. */
  speakChance: 0.55,
  /** Longer authored lines are a CONTENT bug — the atlas cell is this wide, so
   *  a longer line paints outside its bubble. Rejected loudly, see
   *  {@link createSpeech}. */
  maxChars: 26,
  /** Metres between the top of the speaker and the tail. Zero and the tail
   *  grows out of the hat; much more and the bubble floats free of its owner
   *  and, at 52 degrees, appears to belong to whoever is standing behind. */
  headroom: 0.30,
});

/**
 * What a speaker with no content-supplied style gets. `script.styleFor` is
 * contracted never to return null, so this only ever catches a duck type in a
 * test — but a missing style must not be a crash in the renderer's hot path.
 * @type {BubbleStyle}
 */
const FALLBACK_STYLE = Object.freeze({ bg: '#101418', fg: '#f2f4f6', accent: '#7a8794' });

/**
 * The one piece of CONTENT this file names, and it names it the way
 * `Wanted.js#LAW_IDS` does: a def id with a default, asked of the INJECTED
 * faction table rather than hard-coded to a number. A table that exposes
 * `POLICE` directly is preferred; `factionForId` is the documented fallback,
 * and 1 is the last resort for a duck type that has neither.
 */
const POLICE_ID = 'police_officer';

/**
 * WHO SAYS WHAT, from what the actor is doing. PURE — no rng, no manager, no
 * state — which is what lets `tests/speech.test.mjs` pin the whole taxonomy as
 * a truth table instead of by driving a city.
 *
 * `isCivilian`/`isGang`/`isPolice` are resolved BY THE CALLER from the team
 * byte through the injected faction rules; this file must never import
 * `src/game/`, and a boolean triple is the narrowest thing that can cross that
 * line.
 *
 * @param {{isCivilian?: boolean, isGang?: boolean, isPolice?: boolean,
 *   brain?: number, mood?: number, fighting?: boolean, wanted?: number}} view
 * @returns {string|null} A topic key, or null when nobody should speak.
 */
export function topicFor(view) {
  if (!view) return null;
  // A car is drawn empty — see the header. This is first because it outranks
  // every faction: a SEATED policeman is still a policeman and still silent.
  if (view.brain === BRAIN.SEATED) return null;

  if (view.isCivilian) {
    // Panic beats idle: somebody running from a gunshot is not making small
    // talk, and a fleeing civilian is the one bubble the player must not miss.
    if (view.mood === PED.FLEE) return 'civilian.panic';
    if (view.mood === PED.STROLL || view.mood === PED.PAUSE) return 'civilian.idle';
    return null;
  }

  if (view.isPolice) {
    // Combat beats alert beats patrol — the cascade IS the escalation, so an
    // officer under fire never says the line he says while walking a beat.
    if (view.fighting) return 'police.combat';
    if ((view.wanted ?? 0) > 0) return 'police.alert';
    return 'police.patrol';
  }

  if (view.isGang) {
    if (view.fighting) return 'gang.combat';
    return 'gang.idle';
  }

  // Unclassified: the tank, a mission actor on a team nobody named, anything a
  // level invents. Silence is the right default — a wrong line is worse than
  // no line, and the roster is the only gate.
  return null;
}

/**
 * @param {string|null} topic
 * @returns {number} 1 for the topics that must jump the queue, 0 otherwise.
 *   Written out rather than pattern-matched on `.combat`: a future topic ending
 *   in `.combat` that is NOT urgent would silently inherit the priority.
 */
function priorityOf(topic) {
  if (topic === 'civilian.panic') return 1;
  if (topic === 'police.combat') return 1;
  if (topic === 'gang.combat') return 1;
  return 0;
}

/**
 * THE SPEECH SYSTEM.
 *
 * `update` runs in exactly this order, and the order is the contract:
 *
 *  1. **ADVANCE AND RETIRE** what is already open, re-reading every speaker's
 *     position. Before the scan, always: a record that retires on this step
 *     frees its cell on this step, so the scan can immediately reuse it and a
 *     cap of five never behaves like a cap of four.
 *  2. **SCAN** for one new speaker, at {@link SPEECH.scanHz}. After the retire,
 *     for the reason above, and gated on the cap and the gap BEFORE any die is
 *     drawn.
 *
 * There is no step 3. Nothing is emitted, nothing is subscribed.
 *
 * @param {Object} o
 * @param {EnemyManager} [o.manager] Without one there is nobody to speak and
 *   {@link say} is the only door left open.
 * @param {any} [o.bus] Accepted, deliberately unused — see the header.
 * @param {{name?: string, bank: (topic: string) => (string[]|null),
 *   styleFor: (topic: string) => BubbleStyle}} [o.script] The CONTENT
 *   (`game/dialogue.js`). Null makes the system inert: it still reconciles and
 *   retires, but nothing new ever opens and no die is drawn.
 * @param {FactionRules} [o.factions] Null classifies every live non-seated
 *   actor as a civilian, so a dev scene with no faction rules still talks.
 * @param {{level?: number}} [o.wanted] The wanted system, read as
 *   `wanted?.level ?? 0`.
 * @param {string|number} [o.seed='gameplay']
 * @param {number} [o.maxLive] Overrides {@link SPEECH.maxLive} (and `tune`'s).
 * @param {Partial<typeof SPEECH>} [o.tune] Shallow override, merged at
 *   construction. The frozen {@link SPEECH} is never mutated.
 * @returns {Object}
 */
export function createSpeech(o) {
  const {
    manager = null,
    bus = null,
    script = null,
    factions = null,
    wanted = null,
    seed = 'gameplay',
    tune = null,
  } = o ?? {};
  void bus; // see the header: no subscriptions in v1, and that is the feature.

  const S = tune ? { ...SPEECH, ...tune } : SPEECH;
  /** Explicit option wins, then `tune`, then the table. */
  const maxLive = Math.max(1, (o?.maxLive ?? S.maxLive) | 0);

  /**
   * A DEDICATED stream (rule 4). Nothing else in the repo uses the
   * `'dialogue'` salt, so adding a topic or a line can never reshuffle the
   * traffic, the population or the city.
   */
  const rng = streamRng(seed, 'dialogue');

  // ── faction resolution ────────────────────────────────────────────────────
  const CIVILIAN = factions?.CIVILIAN ?? 0;
  const GANGS = Array.isArray(factions?.GANGS) ? factions.GANGS.slice() : [];
  const POLICE = factions?.POLICE ?? factions?.factionForId?.(POLICE_ID) ?? 1;

  // ── the pool ──────────────────────────────────────────────────────────────
  /**
   * Every record that will ever exist, allocated once. `cell` IS the pool
   * index and never changes: freeing the record is therefore exactly what
   * frees the cell, and no second ledger can drift out of step with the first.
   * The renderer gets a stable instance slot per bubble for free.
   *
   * The public half of a record is {@link Bubble}; `t`, `typeIdx`, `typeEnd`,
   * `holdEnd` and `endT` are ours.
   */
  const pool = [];
  for (let k = 0; k < maxLive; k++) {
    pool.push({
      slot: -1,
      cell: k,
      x: 0,
      y: 0,
      z: 0,
      text: '',
      chars: 0,
      alpha: 0,
      style: FALLBACK_STYLE,
      topic: '',
      // ── internal ──
      t: 0,
      typeIdx: -1,
      typeEnd: 0,
      holdEnd: 0,
      endT: 0,
    });
  }
  /** Free cells, a stack. `freeN` is the live count of it. */
  const freeCells = new Int32Array(maxLive);
  let freeN = maxLive;
  for (let k = 0; k < maxLive; k++) freeCells[k] = maxLive - 1 - k;

  /** @type {Array<Bubble>} Live bubbles, newest last. REUSED every step. */
  const bubbles = [];

  // ── per-slot bookkeeping ──────────────────────────────────────────────────
  /**
   * Manager capacity, read once. Taken as the WIDEST of the arrays we index
   * rather than from `state` alone, so a duck type in a test that ships half a
   * manager still gets a scan; a real manager's arrays are all one length and
   * this is that length. A duck type with no arrays at all gets 0 and the scan
   * simply never has anywhere to look.
   */
  const cap = Math.max(
    manager?.state?.length ?? 0,
    manager?.x?.length ?? 0,
    manager?.typeIdx?.length ?? 0,
    manager?.top | 0,
  );
  /**
   * `clock` at which each slot may speak again. Keyed on the raw INDEX, which
   * is safe only because {@link onSlotFreed} clears it: without that, a slot
   * recycled into a fresh pedestrian would inherit the dead one's silence.
   */
  const cooldown = new Float64Array(cap);
  /** Candidate scratch, allocated once — the `Wanted.js#candidates` idiom. */
  const candidates = new Int32Array(Math.max(64, cap));
  /**
   * Last line index handed out per topic, so one bank never says the same
   * thing twice running. A Map, not an object: topics are content-supplied
   * strings and a plain object would give them a prototype to collide with.
   * @type {Map<string, number>}
   */
  const lastLine = new Map();

  // ── counters ──────────────────────────────────────────────────────────────
  let clock = 0;
  let scanAcc = 0;
  let sinceOpen = Infinity;
  let opened = 0;
  let retired = 0;
  let dropped = 0;
  let warned = false;
  let disposed = false;

  const scanPeriod = 1 / Math.max(0.001, S.scanHz);
  const radius2 = S.radius * S.radius;
  /** Hysteresis on the way OUT, so a speaker walking the edge of the ring does
   *  not strobe its own bubble open and shut. */
  const keep2 = (S.radius * 1.35) * (S.radius * 1.35);

  // ── record lifecycle ──────────────────────────────────────────────────────

  /**
   * Drops the record at `k` in {@link bubbles} without allocating — `splice`
   * returns a new array, and this runs in the steady state.
   * @param {number} k
   */
  const removeAt = (k) => {
    const rec = bubbles[k];
    for (let j = k; j < bubbles.length - 1; j++) bubbles[j] = bubbles[j + 1];
    bubbles.length -= 1;
    rec.slot = -1;
    rec.typeIdx = -1;
    rec.text = '';
    rec.chars = 0;
    rec.alpha = 0;
    rec.topic = '';
    freeCells[freeN++] = rec.cell;
    retired++;
  };

  /** @param {number} slot @returns {number} Index in {@link bubbles}, or -1. */
  const indexOfSlot = (slot) => {
    for (let k = 0; k < bubbles.length; k++) if (bubbles[k].slot === slot) return k;
    return -1;
  };

  /**
   * Where the tail tip goes. `manager.y` is the FEET, so the bubble's BOTTOM is
   * feet + the walk bob + the actor's own height + {@link SPEECH.headroom} —
   * off `art.worldHeight`, which is authoritative for every art kind, so a
   * bubble sits correctly over a 1.3 m chibi and over the tank without either
   * of them being named here.
   * @param {Bubble} rec
   */
  const readPos = (rec) => {
    const i = rec.slot;
    const h = manager?.typeOf?.(i)?.worldHeight ?? 1.4;
    rec.x = manager?.x?.[i] ?? 0;
    rec.y = (manager?.y?.[i] ?? 0) + (manager?.wobbleBob?.[i] ?? 0) + h + S.headroom;
    rec.z = manager?.z?.[i] ?? 0;
  };

  /**
   * THE SLOT-IDENTITY BACKSTOP. See the header: the type check cannot see a
   * recycle into the same type, which is what {@link onSlotFreed} is for, and
   * `onSlotFreed` needs a caller, which is what this is for.
   * @param {Bubble} rec @returns {boolean} Still the same person.
   */
  const stillTheirs = (rec) => {
    const i = rec.slot;
    if (i < 0 || !manager) return false;
    if ((manager.state?.[i] ?? STATE.DEAD) === STATE.DEAD) return false;
    if ((manager.typeIdx?.[i] ?? -1) !== rec.typeIdx) return false;
    return true;
  };

  /**
   * Takes a free cell and starts a line. The ONE place a record is opened,
   * from both {@link say} and the scan, so the clocks can never be set two
   * different ways.
   * @param {number} slot @param {string} text @param {string} topic
   * @returns {Bubble|null}
   */
  const open = (slot, text, topic) => {
    if (freeN <= 0) return null;
    const rec = pool[freeCells[--freeN]];
    const n = text.length;
    rec.slot = slot;
    rec.typeIdx = manager?.typeIdx?.[slot] ?? -1;
    rec.text = text;
    rec.topic = topic;
    rec.style = script?.styleFor?.(topic) ?? FALLBACK_STYLE;
    rec.t = 0;
    rec.chars = 0;
    rec.alpha = 0;
    // The three instants the whole animation is read off. Computed ONCE, from
    // the line's own length — see the header on why nothing accumulates.
    rec.typeEnd = n / S.charHz;
    rec.holdEnd = rec.typeEnd + S.holdMin + S.holdPerChar * n;
    rec.endT = rec.holdEnd + S.fadeOut;
    readPos(rec);
    bubbles.push(rec);
    if (slot >= 0 && slot < cap) cooldown[slot] = clock + S.slotCooldown;
    sinceOpen = 0;
    opened++;
    return rec;
  };

  /**
   * An over-long line is a CONTENT bug in `game/dialogue.js` — the atlas cell
   * is {@link SPEECH.maxChars} wide, so the line would paint outside its own
   * bubble. Refusing it silently would leave that bug undiscoverable, so it
   * warns ONCE per system and names both the topic and the line.
   * @param {string} text @param {string} topic @returns {boolean} Rejected.
   */
  const tooLong = (text, topic) => {
    if (text.length <= S.maxChars) return false;
    dropped++;
    if (!warned) {
      warned = true;
      // eslint-disable-next-line no-console
      console.warn(`Speech: line over ${S.maxChars} chars for '${topic}' — `
        + `it would paint outside the bubble: "${text}"`);
    }
    return true;
  };

  // ── the scan ──────────────────────────────────────────────────────────────

  /**
   * @param {number} i @returns {string|null} The topic this slot would speak,
   *   or null. Builds the {@link topicFor} view from the team byte through the
   *   INJECTED rules — with no rules at all everybody is a civilian, which is
   *   what makes a dev scene talk.
   */
  const topicOf = (i) => {
    const team = manager?.team?.[i] ?? CIVILIAN;
    let isPolice = false;
    let isGang = false;
    let isCivilian = true;
    if (factions) {
      isPolice = team === POLICE;
      if (!isPolice) for (let g = 0; g < GANGS.length; g++) if (GANGS[g] === team) { isGang = true; break; }
      isCivilian = !isPolice && !isGang && team === CIVILIAN;
    }
    return topicFor({
      isCivilian,
      isGang,
      isPolice,
      brain: manager?.brain?.[i] ?? BRAIN.NONE,
      // A duck type with no mood array is a STROLLing crowd, not a silent one.
      mood: manager?.mood?.[i] ?? PED.STROLL,
      fighting: (manager?.target?.[i] ?? -1) >= 0,
      wanted: wanted?.level ?? 0,
    });
  };

  /**
   * One candidate sweep and, at most, one opening.
   *
   * ⚠ THE GATES COME FIRST AND THE DICE COME LAST. Everything above the
   * `chance` call is a filter over state; the first die is not touched until
   * there is somebody to speak, a cell to put them in and a gap to open in. An
   * idle frame draws nothing at all — see the header.
   * @param {number} px @param {number} py @param {number} pz
   */
  const scan = (px, py, pz) => {
    if (!script || !manager) return;
    if (bubbles.length >= maxLive) return;
    if (sinceOpen < S.openGap) return;

    const top = manager.top | 0;
    let n = 0;
    let best = -1;
    /** @type {string|null} The topic of the tier currently held. */
    let bestTopic = null;
    for (let i = 0; i < top && i < cap; i++) {
      const st = manager.state?.[i] ?? STATE.DEAD;
      // DYING counts as GONE — the same reading `Shops.js#alive` takes. A last
      // word from somebody in their death shrink is a screenshot.
      if (st === STATE.DEAD || st === STATE.DYING) continue;
      if (manager.inCabin?.[i]) continue;
      if (cooldown[i] > clock) continue;
      const dx = (manager.x?.[i] ?? 0) - px;
      const dy = (manager.y?.[i] ?? 0) - py;
      const dz = (manager.z?.[i] ?? 0) - pz;
      // 3D, and the header says why: a shop is a room 100 m down.
      if (dx * dx + dy * dy + dz * dz > radius2) continue;
      if (indexOfSlot(i) >= 0) continue; // already talking
      const topic = topicOf(i);
      if (!topic) continue;
      const p = priorityOf(topic);
      if (p < best) continue;
      // A higher tier arriving DISCARDS everything below it, in the one pass.
      if (p > best) {
        best = p;
        n = 0;
      }
      bestTopic = topic;
      candidates[n++] = i;
    }
    if (!n) return;

    // ── the dice: exactly three, and only from here down ──
    if (!chance(rng, S.speakChance)) return;
    const slot = candidates[Math.min(n - 1, (rng() * n) | 0)];
    // The chosen slot's own topic, re-read rather than remembered: `bestTopic`
    // is only the tier's last writer, and a tier holds several speakers.
    const topic = topicOf(slot) ?? bestTopic;
    if (!topic) return;
    const bank = script.bank?.(topic);
    if (!Array.isArray(bank) || bank.length === 0) return;

    // ONE die for the line, and the no-repeat is arithmetic on the result
    // rather than a re-roll: a re-roll would spend a variable number of dice
    // and make the stream depend on what was said last.
    let k = Math.floor(rng() * bank.length);
    if (k >= bank.length) k = bank.length - 1;
    if (bank.length > 1 && k === lastLine.get(topic)) k = (k + 1) % bank.length;
    const text = String(bank[k] ?? '');
    if (!text || tooLong(text, topic)) return;
    lastLine.set(topic, k);
    open(slot, text, topic);
  };

  // ── the step ──────────────────────────────────────────────────────────────

  /**
   * One fixed step.
   * @param {number} dt
   * @param {number} px @param {number} py @param {number} pz The PLAYER's world
   *   position (FEET — the same anchor the actors carry, so the ring is
   *   symmetric).
   */
  const update = (dt, px = 0, py = 0, pz = 0) => {
    if (disposed || !(dt > 0)) return;
    clock += dt;
    if (sinceOpen !== Infinity) sinceOpen += dt;

    // ── 1. advance and retire ──────────────────────────────────────────────
    for (let k = bubbles.length - 1; k >= 0; k--) {
      const rec = bubbles[k];
      rec.t += dt;

      // The speaker stopped being the speaker: died, was recycled into somebody
      // else, or got into a car. All three are a HARD drop — there is nobody
      // left to fade out over.
      if (!stillTheirs(rec) || manager?.inCabin?.[rec.slot]) {
        removeAt(k);
        continue;
      }
      readPos(rec);

      const dx = rec.x - px;
      const dy = (manager?.y?.[rec.slot] ?? rec.y) - py;
      const dz = rec.z - pz;
      if (dx * dx + dy * dy + dz * dz > keep2) {
        removeAt(k);
        continue;
      }

      if (rec.t >= rec.endT) {
        removeAt(k);
        continue;
      }

      // Both derived from `rec.t` alone, every step, from the instants fixed at
      // open time. Nothing here can drift.
      const shown = Math.floor(rec.t * S.charHz);
      rec.chars = shown > rec.text.length ? rec.text.length : shown;
      rec.alpha = rec.t < S.fadeIn
        ? (S.fadeIn > 0 ? rec.t / S.fadeIn : 1)
        : rec.t < rec.holdEnd
          ? 1
          : (S.fadeOut > 0 ? (rec.endT - rec.t) / S.fadeOut : 0);
      if (rec.alpha > 1) rec.alpha = 1;
      else if (rec.alpha < 0) rec.alpha = 0;
    }

    // ── 2. scan ────────────────────────────────────────────────────────────
    // An ACCUMULATOR, not a modulo on a wall clock: the sim is fixed-60 but the
    // render rate can halve (`perfGovernor`), and a modulo would sample the
    // same phase forever if the two ever lined up.
    scanAcc += dt;
    if (scanAcc >= scanPeriod) {
      scanAcc -= scanPeriod;
      // A long hitch must not buy a burst of scans it never earned.
      if (scanAcc > scanPeriod) scanAcc = 0;
      scan(px, py, pz);
    }
  };

  /**
   * THE EXPLICIT DOOR. Opens a bubble NOW: no chance roll, no cooldown test —
   * a scripted line is not an ambient one. It still respects the CAP (there are
   * only `maxLive` atlas cells and no sixth one to borrow) and still refuses an
   * over-long line, because that limit is the cell's WIDTH and a `say` that
   * painted outside it would be the same content bug arriving through a
   * different door.
   *
   * A slot that is already talking is INTERRUPTED rather than refused: the old
   * record retires, its cell is freed, and the new line takes it. That is what
   * a dev scene driving one actor through a bank of lines wants, and it means
   * `say` can never fail on the cap for somebody who already owns a cell.
   *
   * @param {number} slot @param {string} text @param {string} [topic='say']
   * @returns {Bubble|null}
   */
  const say = (slot, text, topic = 'say') => {
    if (disposed) return null;
    const line = String(text ?? '');
    if (!line || tooLong(line, topic)) return null;
    const k = indexOfSlot(slot);
    if (k >= 0) removeAt(k);
    if (freeN <= 0) {
      dropped++;
      return null;
    }
    return open(slot, line, topic);
  };

  /**
   * A manager slot went free. THE EXACT SIGNAL — `Gameplay` fans out to this
   * explicitly rather than us chaining `manager.onDespawn`, because `Shops.js`
   * already owns a link in that chain and two systems reassigning one property
   * is how a director's bookkeeping disappears.
   *
   * Clearing the COOLDOWN is half the point: it is keyed on the raw slot index,
   * and a fresh pedestrian landing in a dead one's slot must not inherit its
   * silence.
   * @param {number} i
   */
  const onSlotFreed = (i) => {
    if (i < 0) return;
    const k = indexOfSlot(i);
    if (k >= 0) removeAt(k);
    if (i < cap) cooldown[i] = 0;
  };

  return {
    name: 'speech',
    /** @type {Bubble[]} REUSED — copy the fields, never keep the array. */
    bubbles,
    update,
    say,
    onSlotFreed,

    /**
     * @returns {{live: number, max: number, opened: number, retired: number,
     *   dropped: number, cells: number}} `cells` is counted from the FREE LIST
     *   and must always equal `live` — an independent read of the ledger, so a
     *   leaked cell shows up as a mismatch instead of as a bubble that never
     *   comes back.
     */
    stats() {
      return {
        live: bubbles.length,
        max: maxLive,
        opened,
        retired,
        dropped,
        cells: maxLive - freeN,
      };
    },

    /** Idempotent. Everything retires; the pool goes back to all-free. */
    dispose() {
      if (disposed) return;
      disposed = true;
      for (let k = bubbles.length - 1; k >= 0; k--) removeAt(k);
      bubbles.length = 0;
      freeN = maxLive;
      for (let k = 0; k < maxLive; k++) freeCells[k] = maxLive - 1 - k;
      lastLine.clear();
    },
  };
}
